// Pure structural helpers for inheritance-pattern detection, all operating on
// a TokenisedPedigree + a single diseaseId (per-disease — see plan §12.3).
//
// TGCLOUD-1712 W3.2/W3.4/W3.5. Three themes run through this file:
//
//  1. OBSERVABILITY (decision DR-3). Several inheritance criteria are satisfied
//     by the ABSENCE of a pattern — "no male-to-male transmission", "no paternal
//     transmission", "only males affected", "unaffected parents". An absence is
//     evidence only where the thing was possible to see. Each of those criteria
//     therefore has a paired `*Observability` helper that reports whether the
//     disconfirming observation could have been made in THIS pedigree, and the
//     detectors may only count the absence when it could.
//  2. PLACEHOLDERS (PA-7). A dummy node is a layout artefact, not a person. It
//     is excluded from every affected-set, parent-set and child-set here, so a
//     placeholder carrying a diagnosis can neither be an affected relative nor
//     make a real relative's parents look "recorded".
//  3. GENERATIONS, NOT PAIRS (PA-1). `verticalPairs` counts parent→child pairs.
//     Two affected parents of one affected child is TWO pairs but only TWO
//     generations, and it is classic recessive pseudodominance — so anything
//     that means "a chain across generations" must use
//     `affectedChainGenerations`, never a pair count.

//  4. HERITABLE MOLECULAR EVIDENCE (W3.4 / PA-3). A pathogenic variant only
//     licenses a Mendelian claim about a disease when the gene is associated
//     with THAT disease (`meta.associatedGenes`) and the variant could have been
//     inherited (`germlineInterpretation`, from tokenise.js). Both checks are
//     needed and neither is redundant: JAK2 genuinely IS associated with primary
//     myelofibrosis, so only the origin separates an acquired driver from a
//     heritable cause; and no origin field would have stopped a BRCA1 carrier's
//     asthma being upgraded.

// Raw predicate: does this individual carry the diagnosis? Placeholder-blind on
// purpose — `detectors/cascade.js` and `detectors/alerts.js` import it for
// index/carrier selection and apply their own `isDummy` rules. Inheritance
// reasoning uses `isAffectedRelative` instead.
export const isAffectedBy = (ind, diseaseId) =>
  !!ind && ind.affectedDiseases.has(diseaseId);

// PA-7: a dummy is a placeholder drawn to hold a position (an unrecorded
// partner, a stub parent). It is not a person who was assessed, so it can never
// be an affected relative however its record is populated.
export const isRealPerson = (ind) => !!ind && !ind.isDummy;

export const isAffectedRelative = (ind, diseaseId) =>
  isRealPerson(ind) && isAffectedBy(ind, diseaseId);

export const affectedList = (ped, diseaseId) =>
  ped.individuals.filter((i) => isAffectedRelative(i, diseaseId));

const get = (ped, id) => (id ? ped.byPid.get(id) || null : null);
const real = (ind) => (isRealPerson(ind) ? ind : null);

export const fatherOf = (ped, ind) => real(get(ped, ind && ind.fatherId));
export const motherOf = (ped, ind) => real(get(ped, ind && ind.motherId));

// The child's MALE parent, by recorded sex rather than by slot (TGCLOUD-1786).
//
// `fatherId`/`motherId` are slots, and until 1786 the questionnaire derivations
// filled them from gender IDENTITY: a non-binary AFAB or Male-AFAB proband (sex
// female) was stored as the father and her male partner as the mother. Every
// male-to-male criterion is a claim about two MALES, so it must not trust the
// slot. Trusting it both invented a transmission (a woman in the father slot)
// and hid a real one (an affected man in the mother slot of an affected son,
// which left X-linked recessive standing when it should be excluded).
//
//  - a parent recorded male, the father slot first when both are;
//  - otherwise the father-slot occupant, but only when their sex is not
//    recorded — the slot is then the only evidence there is;
//  - never anyone recorded female, whichever slot they sit in.
//
// Where the slots agree with recorded sex this is exactly `fatherOf`, so the
// analyser's output for those families is unchanged.
export const maleParentOf = (ped, ind) => {
  const father = fatherOf(ped, ind);
  if (father && father.sex === 'M') return father;
  const mother = motherOf(ped, ind);
  if (mother && mother.sex === 'M') return mother;
  return father && father.sex !== 'F' ? father : null;
};

// Recorded (non-placeholder) parents. `parents.length > 0` is therefore a real
// structural statement — "this individual has at least one parent on the
// pedigree whose phenotype could have been recorded".
export const parentsOf = (ped, ind) =>
  [fatherOf(ped, ind), motherOf(ped, ind)].filter(Boolean);

export const childrenOf = (ped, ind) =>
  ((ind && ind.childrenIds) || [])
    .map((id) => get(ped, id))
    .filter(isRealPerson);

export const siblingsOf = (ped, ind) =>
  ((ind && ind.siblingIds) || [])
    .map((id) => get(ped, id))
    .filter(isRealPerson);

export const sonsOf = (ped, ind) =>
  childrenOf(ped, ind).filter((c) => c.sex === 'M');

export const daughtersOf = (ped, ind) =>
  childrenOf(ped, ind).filter((c) => c.sex === 'F');

export const brothersOf = (ped, ind) =>
  siblingsOf(ped, ind).filter((s) => s.sex === 'M');

export const affectedMales = (ped, diseaseId) =>
  affectedList(ped, diseaseId).filter((i) => i.sex === 'M');

export const affectedFemales = (ped, diseaseId) =>
  affectedList(ped, diseaseId).filter((i) => i.sex === 'F');

export const pidsOf = (list) => list.map((i) => i.pid);

const uniq = (values) => [...new Set(values)];

// ---------------------------------------------------------------------------
// Vertical transmission
// ---------------------------------------------------------------------------

// Count (affected parent → affected child) PAIRS. Deliberately narrow: this is
// a count of transmission events, NOT a count of generations. See PA-1 at the
// head of this file before using it for anything that means "multi-generation".
export function verticalPairs(ped, diseaseId) {
  let count = 0;
  for (const child of affectedList(ped, diseaseId))
    for (const parent of parentsOf(ped, child))
      if (isAffectedRelative(parent, diseaseId)) count++;
  return count;
}

// The longest chain of affected individuals linked parent→child, measured in
// GENERATIONS spanned (1 = affected individuals but no transmission observed,
// 2 = one parent→child transmission, 3 = grandparent→parent→child).
//
// This is the honest measure of "vertical transmission across generations".
// `verticalPairs >= 2` was not: it also counts one affected child of two
// affected parents (2 pairs, 2 generations — pseudodominance, which is
// EXPECTED under recessive inheritance) and one affected parent with two
// affected children (2 pairs, 2 generations — a single transmission each).
export function affectedChainGenerations(ped, diseaseId) {
  const affected = affectedList(ped, diseaseId);
  if (!affected.length) return 0;
  const inChain = new Set(pidsOf(affected));
  const memo = new Map();

  // Longest downward run of affected individuals starting at pid. Pedigree
  // parent edges form a DAG, so the memo is safe; the path guard is belt and
  // braces against a corrupt cycle and simply stops the walk.
  const down = (pid, path) => {
    if (memo.has(pid)) return memo.get(pid);
    if (path.has(pid)) return 1;
    path.add(pid);
    let best = 1;
    for (const child of childrenOf(ped, get(ped, pid)))
      if (inChain.has(child.pid))
        best = Math.max(best, 1 + down(child.pid, path));
    path.delete(pid);
    memo.set(pid, best);
    return best;
  };

  let longest = 0;
  for (const ind of affected)
    longest = Math.max(longest, down(ind.pid, new Set()));
  return longest;
}

// Affected individuals BOTH of whose recorded parents are affected. Recessive
// pseudodominance: an affected × affected mating produces affected children
// under recessive inheritance, and this configuration must never be read as
// evidence against it (PA-1).
export function affectedWithBothParentsAffected(ped, diseaseId) {
  return affectedList(ped, diseaseId).filter((child) => {
    const parents = parentsOf(ped, child);
    return (
      parents.length === 2 &&
      parents.every((p) => isAffectedRelative(p, diseaseId))
    );
  });
}

// (affected mother → affected child) transmission events.
export function maternalTransmissionPairs(ped, diseaseId) {
  const pairs = [];
  for (const mother of affectedFemales(ped, diseaseId))
    for (const child of childrenOf(ped, mother))
      if (isAffectedRelative(child, diseaseId))
        pairs.push({ parent: mother.pid, child: child.pid });
  return pairs;
}

// (affected father → affected child) transmission events.
export function paternalTransmissionPairs(ped, diseaseId) {
  const pairs = [];
  for (const father of affectedMales(ped, diseaseId))
    for (const child of childrenOf(ped, father))
      if (isAffectedRelative(child, diseaseId))
        pairs.push({ parent: father.pid, child: child.pid });
  return pairs;
}

export const hasMaternalTransmission = (ped, diseaseId) =>
  maternalTransmissionPairs(ped, diseaseId).length > 0;

export const hasPaternalTransmission = (ped, diseaseId) =>
  paternalTransmissionPairs(ped, diseaseId).length > 0;

// An affected male whose father is also affected (excludes X-linked / MT). The
// father is the son's MALE parent, not whoever is in the father slot — see
// `maleParentOf`.
export function hasMaleToMaleTransmission(ped, diseaseId) {
  for (const son of affectedMales(ped, diseaseId)) {
    const father = maleParentOf(ped, son);
    if (father && isAffectedRelative(father, diseaseId)) return true;
  }
  return false;
}

export function bothSexesAffected(ped, diseaseId) {
  const a = affectedList(ped, diseaseId);
  return a.some((i) => i.sex === 'M') && a.some((i) => i.sex === 'F');
}

// ---------------------------------------------------------------------------
// Observability of the absence-based criteria (DR-3)
// ---------------------------------------------------------------------------

// Could male-to-male transmission have been OBSERVED here?
//
// It could, if some affected male has a recorded father (the father could have
// carried the diagnosis) or recorded sons (a son could have carried it). With
// neither, "no male-to-male transmission" says nothing about the disease — it
// says the pedigree does not reach far enough to show it, and it must not be
// offered as support for X-linked or mitochondrial inheritance.
//
// "Recorded father" means a male parent (`maleParentOf`): a woman stored in the
// father slot could never have shown male-to-male transmission.
export function maleToMaleObservability(ped, diseaseId) {
  const withFather = [];
  const withSons = [];
  for (const male of affectedMales(ped, diseaseId)) {
    if (maleParentOf(ped, male)) withFather.push(male.pid);
    if (sonsOf(ped, male).length) withSons.push(male.pid);
  }
  return {
    observable: withFather.length > 0 || withSons.length > 0,
    affectedMalesWithRecordedFather: withFather,
    affectedMalesWithRecordedSons: withSons
  };
}

// Could paternal transmission have been OBSERVED here? It could, if some
// affected male has a recorded child (he could have transmitted to them), or
// some affected individual has a recorded father (the father could have been
// affected). With neither, "no paternal transmission" is not evidence for
// mitochondrial inheritance.
//
// "Recorded father" is the MALE parent (`maleParentOf`), not the father slot:
// a woman stored there could never have shown paternal transmission, and a man
// stored in the mother slot could (TGCLOUD-1786).
export function paternalTransmissionObservability(ped, diseaseId) {
  const affectedFathersWithChildren = [];
  const affectedWithRecordedFather = [];
  for (const ind of affectedList(ped, diseaseId)) {
    if (ind.sex === 'M' && childrenOf(ped, ind).length)
      affectedFathersWithChildren.push(ind.pid);
    if (maleParentOf(ped, ind)) affectedWithRecordedFather.push(ind.pid);
  }
  return {
    observable:
      affectedFathersWithChildren.length > 0 ||
      affectedWithRecordedFather.length > 0,
    affectedFathersWithChildren,
    affectedWithRecordedFather
  };
}

// Could an affected FEMALE have been observed here? Only if the pedigree
// records female relatives of the affected individuals at all. "Only males
// affected" and "no affected females" are otherwise statements about who was
// drawn, not about the disease — which is how two affected males in a
// male-only fragment used to reach X-linked recessive and Y-linked support.
export function affectedFemaleObservability(ped, diseaseId) {
  const females = new Set();
  for (const ind of affectedList(ped, diseaseId)) {
    for (const rel of [
      ...parentsOf(ped, ind),
      ...siblingsOf(ped, ind),
      ...childrenOf(ped, ind)
    ])
      if (rel.sex === 'F' && !isAffectedRelative(rel, diseaseId))
        females.add(rel.pid);
  }
  return {
    observable: females.size > 0,
    unaffectedFemaleRelatives: [...females]
  };
}

// Was this individual's phenotype ASCERTAINED, or is the record merely silent?
//
// "Affected individual with unaffected parents" is the core recessive criterion
// and in this data model "unaffected" means only "no diagnosis was entered".
// A parent drawn to complete the pedigree, with nothing recorded about them, is
// not an observation of an unaffected parent.
//
// Ascertainment is taken as CLINICAL content: a recorded diagnosis (for any
// disease), a recorded phenotype term, a recorded genotype, or a recorded
// carrier status. Deliberately NOT demographics — a date of birth makes a node
// a documented person but says nothing about whether anyone assessed them; and
// deliberately NOT a recorded death, since a relative who died unassessed is
// exactly the under-ascertained case this check exists to catch.
//
// Limitation, worth knowing when reading a result: the tokeniser only keeps
// diagnoses flagged `displayOnPedigree`, so a diagnosis recorded but not
// displayed does not count as ascertainment here.
export function phenotypeAscertained(ind) {
  if (!isRealPerson(ind)) return false;
  return (
    ind.affectedDiseases.size > 0 ||
    (ind.phenotypes || []).length > 0 ||
    (ind.genotypes || []).length > 0 ||
    Object.keys(ind.carrierByDisease || {}).length > 0
  );
}

// Affected individuals with ≥1 RECORDED parent, none of whom is affected.
// Structural only — see `affectedWithAscertainedUnaffectedParents` for the
// version that also requires the parents to have been assessed.
export function affectedWithUnaffectedParents(ped, diseaseId) {
  return affectedList(ped, diseaseId).filter((child) => {
    const parents = parentsOf(ped, child);
    return (
      parents.length > 0 &&
      parents.every((p) => !isAffectedRelative(p, diseaseId))
    );
  });
}

// The same set, restricted to individuals whose every recorded parent has
// actual clinical content on the record.
export function affectedWithAscertainedUnaffectedParents(ped, diseaseId) {
  return affectedWithUnaffectedParents(ped, diseaseId).filter((child) =>
    parentsOf(ped, child).every((p) => phenotypeAscertained(p))
  );
}

// Sibships with ≥2 affected sharing the same (unaffected) recorded parents.
// Returns the sibships themselves so a detector can say which, and whether the
// parents of each were actually assessed.
export function affectedSibshipsWithUnaffectedParents(ped, diseaseId) {
  const byParentKey = new Map();
  for (const ind of affectedWithUnaffectedParents(ped, diseaseId)) {
    const key = [ind.fatherId, ind.motherId].sort().join('|');
    if (!byParentKey.has(key))
      byParentKey.set(key, {
        parentKey: key,
        pids: [],
        parentsAscertained: parentsOf(ped, ind).every((p) =>
          phenotypeAscertained(p)
        )
      });
    byParentKey.get(key).pids.push(ind.pid);
  }
  return [...byParentKey.values()].filter((s) => s.pids.length >= 2);
}

// ---------------------------------------------------------------------------
// Consanguinity — scoped to the affected individuals' own ancestry (PA-6)
// ---------------------------------------------------------------------------

export const hasConsanguinity = (ped) =>
  (ped.consanguinityLoops || []).length > 0;

export function ancestorPidsOf(ped, pid) {
  const out = new Set();
  const stack = [pid];
  while (stack.length) {
    const cur = get(ped, stack.pop());
    if (!cur) continue;
    for (const parent of [cur.fatherId, cur.motherId]) {
      if (parent && ped.byPid.has(parent) && !out.has(parent)) {
        out.add(parent);
        stack.push(parent);
      }
    }
  }
  return out;
}

// The consanguineous unions that actually sit in an affected individual's
// ancestry. `hasConsanguinity(ped)` is whole-pedigree, so a cousin marriage in
// one branch used to elevate the recessive prior for every disease in the
// family, including diagnoses carried only by people descended from neither
// partner (PA-6).
export function consanguineousLoopsInAffectedAncestry(ped, diseaseId) {
  const loops = ped.consanguinityLoops || [];
  if (!loops.length) return [];
  const affected = affectedList(ped, diseaseId);
  if (!affected.length) return [];
  const ancestries = affected.map((a) => ({
    pid: a.pid,
    ancestors: ancestorPidsOf(ped, a.pid)
  }));
  return loops.filter(({ partners }) => {
    const [a, b] = partners || [];
    if (!a || !b) return false;
    // Both partners must be ancestors of the same affected individual: it is
    // descent from the union that raises the chance of two copies. A
    // consanguineous couple who are themselves affected, or whose union sits
    // beside the affected lineage, does not.
    return ancestries.some(
      ({ ancestors }) => ancestors.has(a) && ancestors.has(b)
    );
  });
}

// ---------------------------------------------------------------------------
// X-linked positive evidence
// ---------------------------------------------------------------------------

// Unaffected females with ≥2 affected male first-degree relatives among their
// father, brothers and sons — the obligate/likely-carrier configuration that
// LINKS affected males to each other through a female.
//
// This is the positive evidence X-linked recessive inference actually needs.
// Two affected males and the absence of male-to-male transmission is not: it is
// equally the picture of two men who happen to share a diagnosis.
//
// Note this deliberately does NOT require the linking female to be
// ascertained-unaffected the way the recessive criterion requires of parents.
// The claim here rests on the TOPOLOGY (she has affected males on both sides of
// her), not on her being unaffected.
//
// Her father is her MALE parent (`maleParentOf`), not whoever holds the father
// slot (TGCLOUD-1786). Slots stored inverted by the pre-1786 FHQ derivation
// otherwise HID a real link (an affected man in her mother slot) or INVENTED
// one (an affected woman in her father slot, reported as an affected male).
export function carrierLinkingFemales(ped, diseaseId) {
  const out = [];
  for (const female of ped.individuals) {
    if (!isRealPerson(female) || female.sex !== 'F') continue;
    if (isAffectedBy(female, diseaseId)) continue;
    const linked = uniq(
      [
        maleParentOf(ped, female),
        ...brothersOf(ped, female),
        ...sonsOf(ped, female)
      ]
        .filter((r) => r && isAffectedRelative(r, diseaseId))
        .map((r) => r.pid)
    );
    if (linked.length >= 2) out.push({ pid: female.pid, linkedPids: linked });
  }
  return out;
}

// The X-linked DOMINANT signature: an affected father transmits to every
// daughter and to no son. Without a reachable positive signature XLD could
// never exceed "moderate", so X-linked recessive out-competed it on ranking
// for every pedigree, whatever the evidence looked like (X5).
export function affectedFatherDaughterSignature(ped, diseaseId) {
  for (const father of affectedMales(ped, diseaseId)) {
    const daughters = daughtersOf(ped, father);
    if (!daughters.length) continue;
    if (!daughters.every((d) => isAffectedRelative(d, diseaseId))) continue;
    const sons = sonsOf(ped, father);
    return {
      pid: father.pid,
      daughters: pidsOf(daughters),
      sons: pidsOf(sons),
      // Only informative when he actually has sons on the pedigree.
      noAffectedSonsObserved:
        sons.length > 0 && sons.every((s) => !isAffectedRelative(s, diseaseId))
    };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Molecular evidence (W3.4 / PA-3)
// ---------------------------------------------------------------------------

// mtDNA genes use the HGNC "MT-" prefix; everything else is nuclear.
const isMitochondrialGene = (gene) => /^MT-/i.test(String(gene || '').trim());

/**
 * The classification an INHERITANCE rule may act on for one gene record.
 *
 * `tokenise.normaliseGenotype` publishes two aggregates per gene:
 *   - `interpretation`         — strongest across all variants, any origin. What
 *                                display and alerting use. Not this.
 *   - `germlineInterpretation` — strongest across variants that could have been
 *                                inherited (origin !== 'somatic'). This one.
 * plus `hasSomaticPathogenic`, so a detector can say WHY it declined.
 *
 * `unknown` origin counts as potentially heritable and does license an upgrade
 * (decision recorded in tokenise.js): almost every stored record predates the
 * origin field, so excluding unknowns would silently withdraw molecular
 * evidence from every pedigree in the system, and the ordinary case for a
 * variant recorded on a family record is germline. Somatic has to be positively
 * stated to be acted on — the direction that fails toward today's behaviour.
 *
 * The fallback exists so a hand-built (un-tokenised) genotype cannot smuggle a
 * somatic driver in through `interpretation`.
 */
const heritableCall = (g) => {
  if (!g) return 'unknown';
  if (g.germlineInterpretation !== undefined) return g.germlineInterpretation;
  if (g.hasSomaticPathogenic === true) return 'unknown';
  return g.interpretation || 'unknown';
};

/**
 * Pathogenic nuclear genes in affected individuals that are ACTUALLY ASSOCIATED
 * with this disease and could have been inherited.
 *
 * The predecessor (`affectedPathogenicNuclearGenes`) matched any pathogenic gene
 * in any affected person against every disease in the family, so a BRCA1 carrier
 * who also had asthma upgraded ASTHMA to "autosomal dominant, strong" (PA-3).
 *
 * @param {object} ped tokenised pedigree
 * @param {string} diseaseId
 * @param {object|null} meta disease metadata; `meta.associatedGenes` is an array
 *   of uppercase HGNC symbols. Missing or empty means NO ASSOCIATION HAS BEEN
 *   ESTABLISHED, so nothing is licensed — the fail-safe direction, since what is
 *   being licensed is the strengthening of a clinical claim.
 * @returns {{
 *   genes: string[],                 // license a Mendelian inference
 *   somaticExcluded: string[],       // pathogenic + associated, but somatic
 *   unassociatedExcluded: string[],  // pathogenic but not linked to THIS disease
 *   hasAssociationData: boolean      // was there anything to check against?
 * }}
 */
export function diseaseAssociatedPathogenicGenes(ped, diseaseId, meta) {
  const associated = new Set(
    (meta && Array.isArray(meta.associatedGenes) ? meta.associatedGenes : [])
      .map((g) =>
        String(g || '')
          .trim()
          .toUpperCase()
      )
      .filter(Boolean)
  );

  const genes = [];
  const somaticExcluded = [];
  const unassociatedExcluded = [];

  for (const ind of affectedList(ped, diseaseId)) {
    for (const g of ind.genotypes || []) {
      if (!g || !g.gene) continue;
      if (isMitochondrialGene(g.gene)) continue;

      const heritablyPathogenic = heritableCall(g) === 'pathogenic';
      const somaticOnly =
        !heritablyPathogenic && g.hasSomaticPathogenic === true;
      if (!heritablyPathogenic && !somaticOnly) continue;

      if (!associated.has(String(g.gene).trim().toUpperCase())) {
        unassociatedExcluded.push(g.gene);
        continue;
      }
      if (somaticOnly) {
        somaticExcluded.push(g.gene);
        continue;
      }
      genes.push(g.gene);
    }
  }

  return {
    genes: uniq(genes),
    somaticExcluded: uniq(somaticExcluded),
    unassociatedExcluded: uniq(unassociatedExcluded),
    hasAssociationData: associated.size > 0
  };
}
